import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { extractMessage } from '@/lib/api-error';
import {
    provisioningService,
    isTerminalJobStatus,
    type ProvisionDeploymentDto,
    type ProvisioningJob,
} from '@/services/provisioning';
import { deploymentKeys } from './deployment.keys';

export function useProvisioningJobs(deploymentId: string | undefined) {
    return useQuery<ProvisioningJob[]>({
        queryKey: deploymentKeys.provisioningJobs(deploymentId),
        queryFn: () => provisioningService.getJobs(deploymentId!),
        enabled: !!deploymentId,
    });
}

export function useProvisioningJob(jobId: string | undefined) {
    return useQuery<ProvisioningJob>({
        queryKey: deploymentKeys.provisioningJob(jobId),
        queryFn: () => provisioningService.getJob(jobId!),
        enabled: !!jobId,
        // Stop polling once there is nothing left to change.
        refetchInterval: (query) =>
            isTerminalJobStatus(query.state.data?.status) ? false : 5_000,
    });
}

export function useProvisionDeployment(deploymentId: string | undefined) {
    const queryClient = useQueryClient();

    return useMutation<ProvisioningJob, unknown, ProvisionDeploymentDto>({
        mutationFn: (data) => provisioningService.provision(deploymentId!, data),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: deploymentKeys.detail(deploymentId) });
            queryClient.invalidateQueries({
                queryKey: deploymentKeys.provisioningJobs(deploymentId),
            });
            toast.success('Provisioning started');
        },
        onError: (err) =>
            toast.error(extractMessage(err, 'Could not start provisioning')),
    });
}

export function useCancelProvisioning(deploymentId: string | undefined) {
    const queryClient = useQueryClient();

    return useMutation<ProvisioningJob, unknown, string>({
        mutationFn: (jobId) => provisioningService.cancel(jobId),
        onSuccess: (job) => {
            queryClient.invalidateQueries({ queryKey: deploymentKeys.detail(deploymentId) });
            queryClient.invalidateQueries({
                queryKey: deploymentKeys.provisioningJobs(deploymentId),
            });
            queryClient.invalidateQueries({
                queryKey: deploymentKeys.provisioningJob(job.id),
            });
            toast.success('Provisioning cancelled');
        },
        onError: (err) => toast.error(extractMessage(err, 'Could not cancel the run')),
    });
}

export function useSshCredential(deploymentId: string | undefined) {
    return useQuery({
        queryKey: deploymentKeys.sshCredential(deploymentId),
        queryFn: () => provisioningService.getSshCredential(deploymentId!),
        enabled: !!deploymentId,
    });
}

export function useSetSshCredential(deploymentId: string | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (cred: { type: 'PASSWORD' | 'PRIVATE_KEY'; secret: string }) =>
            provisioningService.setSshCredential(deploymentId!, cred),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: deploymentKeys.sshCredential(deploymentId),
            });
            toast.success('SSH credential saved');
        },
        onError: (err) => toast.error(extractMessage(err, 'Could not save the credential')),
    });
}

export function useClearSshCredential(deploymentId: string | undefined) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => provisioningService.clearSshCredential(deploymentId!),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: deploymentKeys.sshCredential(deploymentId),
            });
            toast.success('Reverted to the fleet key');
        },
        onError: (err) => toast.error(extractMessage(err, 'Could not clear the credential')),
    });
}
